import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { rowHasIssue } from '../../utils/issueFieldUtils';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

function RiserHealthChart({ unitsData, hvacType = "fancoil", dataFormat = "old" }) {
  // All potential issue columns (must match server's issue_columns array)
  const issueCols = [
    'thermostat',
    'blower_fan',
    'actuator',
    'n3way_valves',
    'drip_pan__sensor',  // Server returns this key, but we check 'drain_pan' field in rawData
    'valves',
    'hoses',
    'mould',
    'coils',
    'compressor',
    'glycol_leakage',
    'leak_sensor',
    'insulation'
  ];

  // Friendly labels
  const friendlyLabels = {
    thermostat:       'Thermostat',
    blower_fan:       'Blower Fan',
    actuator:         'Actuator',
    n3way_valves:     '3 Way Valves',
    drip_pan__sensor: 'Drain Pan',  // Display as "Drain Pan" but use drip_pan__sensor key
    valves:           'Valves',
    hoses:            'Hoses',
    mould:            'Potential Mould',
    coils:            'Coils',
    compressor:       'Compressor',
    glycol_leakage:   'Glycol Leakage',
    leak_sensor:      'Leak Sensor',
    insulation:       'Insulation'
  };

  // Color palette
  const COLOR_PALETTE = [
    '#224982', // Thermostat
    '#2B59A2', // Blower Fan
    '#3369C2', // Actuator
    '#3B79E2', // 3 Way Valves
    '#4479FA', // Drain Pan
    '#4C88FA', // Valves
    '#5796FA', // Hoses
    '#64A4FA', // Mould
    '#75B2FA', // Coils
    '#86C0FA', // Compressor
    '#9FCDFC', // Glycol
    '#5C88FA', // Leak Sensor
    '#B0D4FC', // Insulation
  ];

  // Data structures
  const riserAllRows = {};
  const fractionalIssueCounts = {};
  const actualIssueCounts = {};

  // Helper to parse last two digits => riser
  // Handles both numeric units (e.g., "1928" -> "28") and alphanumeric units (e.g., "PH20" -> "20", "PH08" -> "08")
  function getRiserKey(unitStr) {
    if (!unitStr) return null;
    
    // Try to extract last two digits from the string
    const match = unitStr.toString().match(/(\d{2})$/);
    if (match) {
      return match[1]; // Last two digits, already padded
    }
    
    // Fallback: try parsing as integer (for purely numeric units)
    const parsed = parseInt(unitStr, 10);
    if (!isNaN(parsed)) {
      return String(parsed % 100).padStart(2, '0');
    }
    
    return null;
  }

  // 1) Gather basic info
  unitsData.forEach((row) => {
    const riser = getRiserKey(row.unit);
    if (!riser) {
      // Debug: Log units that don't get a riser key
      if (row.unit && (row.unit.toString().toUpperCase().includes('PH') || row.unit.toString().toUpperCase().includes('LP'))) {
        console.log(`[RiserHealthChart] Unit ${row.unit} did not get a riser key`);
      }
      return;
    }
    
    // Debug: Log riser assignment for PH/LP units
    if (row.unit && (row.unit.toString().toUpperCase().includes('PH') || row.unit.toString().toUpperCase().includes('LP'))) {
      console.log(`[RiserHealthChart] Unit ${row.unit} assigned to riser ${riser}`);
    }

    // Initialize if missing
    if (!riserAllRows[riser]) {
      riserAllRows[riser] = [];
      fractionalIssueCounts[riser] = {};
      actualIssueCounts[riser] = {};
      issueCols.forEach((col) => {
        fractionalIssueCounts[riser][col] = 0;
        actualIssueCounts[riser][col] = 0;
      });
    }

    riserAllRows[riser].push(row);

    // If row.inspection === 'OK', no overall issue
    if (row.inspection === 'OK') return;

    // Detect issues based on format
    let found = [];
    if (dataFormat === "new" && row.rawData) {
      // Debug: Log for units with drain pan in issues list
      if (row.issues && row.issues.includes && row.issues.includes('Drain Pan')) {
        console.log(`[RiserHealthChart] Unit ${row.unit}: Has 'Drain Pan' in issues list, checking rawData...`);
        console.log(`[RiserHealthChart] Unit ${row.unit}: rawData keys:`, Object.keys(row.rawData).filter(k => k.toLowerCase().includes('drain') || k.toLowerCase().includes('drip') || k.toLowerCase().includes('pan')));
      }
      
      // New format: check if issue fields contain "HAS ISSUES" or 🔴
      // Map old column names to possible new field name keywords
      const fieldKeywordMap = {
        'thermostat': ['thermostat'],
        'blower_fan': ['blower_fan'],
        'actuator': ['actuator'],
        'n3way_valves': ['3_way_valve', '3_way_valves', 'field_3_way_valves', 'n3way_valves'],
        'drain_pan': ['drain_pan'], // Will be handled separately for drain pan
        'valves': ['valves'],
        'hoses': ['hoses'],
        'mould': ['mould'],
        'coils': ['coils'],
        'compressor': ['compressor'],
        'glycol_leakage': ['glycol_leakage'],
        'leak_sensor': ['leak_sensor'],
        'insulation': ['insulation']
      };
      
      // Direct field name mapping for exact matches (for fields like "drain_pan" that are arrays)
      const directFieldMap = {
        'drip_pan__sensor': ['drain_pan'] // Column is 'drip_pan__sensor' but field in rawData is 'drain_pan'
      };
      
      // Description field mapping for drain pan
      const descriptionFieldMap = {
        'drip_pan__sensor': 'description_of_drain_pan_issue_be_specific'
      };
      
      found = issueCols.filter((col) => {
        const keywords = fieldKeywordMap[col] || [col];
        const directFields = directFieldMap[col] || [];
        const descriptionField = descriptionFieldMap[col];
        let hasIssue = false;
        let hasExplicitOK = false;
        
        // Special handling for drain pan (col is 'drip_pan__sensor' but we check 'drain_pan' field in rawData)
        if (col === 'drip_pan__sensor') {
          // Debug logging - log for ALL units to see which ones are being checked
          console.log(`[RiserHealthChart] Checking drain_pan for unit ${row.unit}, inspection: ${row.inspection}`);
          
          if (!row.rawData) {
            console.warn('[RiserHealthChart] No rawData for unit:', row.unit);
            return false;
          }
          
          // First, check the drain_pan array field directly
          if (row.rawData.hasOwnProperty('drain_pan')) {
            const val = row.rawData['drain_pan'];
            console.log(`[RiserHealthChart] Unit ${row.unit}: drain_pan field found:`, val);
            if (val !== null && val !== undefined && val !== '') {
              let valStr = Array.isArray(val) ? val.join(" ") : String(val);
              valStr = valStr.toUpperCase().trim();
              
              console.log(`[RiserHealthChart] Unit ${row.unit}: drain_pan value string:`, valStr);
              
              // Check for explicit issue indicators
              if (valStr.includes("HAS ISSUES") || valStr.includes("🔴")) {
                console.log(`[RiserHealthChart] Unit ${row.unit}: Found drain pan issue!`);
                return true; // Found explicit issue
              }
              
              // If explicitly "OK", this is NOT an issue
              if (valStr.includes("OK") && valStr.includes("🟢")) {
                console.log(`[RiserHealthChart] Unit ${row.unit}: drain_pan is OK`);
                return false; // Explicitly OK
              }
            }
          } else {
            // Log what fields are actually in rawData
            const drainPanFields = Object.keys(row.rawData).filter(key => 
              key.toLowerCase().includes('drain') || key.toLowerCase().includes('drip') || key.toLowerCase().includes('pan')
            );
            console.log(`[RiserHealthChart] Unit ${row.unit}: No 'drain_pan' field. Related fields:`, drainPanFields);
          }
          
          // Second, check the description field
          if (descriptionField && row.rawData.hasOwnProperty(descriptionField)) {
            const val = row.rawData[descriptionField];
            if (val !== null && val !== undefined && val !== '') {
              let valStr = Array.isArray(val) ? val.join(" ") : String(val);
              valStr = valStr.trim();
              
              // If there's a non-empty description, it indicates an issue
              if (valStr.length > 0 && !valStr.toUpperCase().includes("NO ISSUES")) {
                console.log(`[RiserHealthChart] Unit ${row.unit}: Found drain pan issue via description field!`);
                return true;
              }
            }
          }
          
          // If neither field indicates an issue, return false
          return false;
        }
        
        // For other issue types, check all keys in rawData for fields that contain any of the keywords
        for (const key in row.rawData) {
          const keyLower = key.toLowerCase();
          // Skip photo fields - they don't indicate issues by themselves
          if (keyLower.includes('photo') || keyLower.includes('image')) {
            continue;
          }
          
          // Check if this key contains any of the keywords for this issue type
          const matchesKeyword = keywords.some(keyword => {
            const keywordLower = keyword.toLowerCase();
            // Check if key contains the keyword (e.g., "drip_pan" in "drip_pan_sensor_condition")
            return keyLower.includes(keywordLower);
          });
          
          if (matchesKeyword) {
            const val = row.rawData[key];
            if (val !== null && val !== undefined && val !== '') {
              let valStr = Array.isArray(val) ? val.join(" ") : String(val);
              valStr = valStr.toUpperCase().trim();
              
              // Check for explicit issue indicators
              if (valStr.includes("HAS ISSUES") || valStr.includes("🔴")) {
                hasIssue = true;
                break; // Found explicit issue, no need to check further
              }
              
              // If explicitly "OK", mark that we found an OK status
              if (valStr === "OK" || (valStr.includes("OK") && valStr.includes("🟢"))) {
                hasExplicitOK = true;
                // Don't break - continue checking other fields for explicit issues
              }
            }
          }
        }
        
        // If we found an explicit issue, return true
        if (hasIssue) {
          return true;
        }
        
        // If we found explicit OK, this is NOT an issue
        if (hasExplicitOK) {
          return false;
        }
        
        return false;
      });
    } else {
      // Old format: check if boolean/string flag indicates an issue
      found = issueCols.filter((col) => rowHasIssue(row, col));
    }
    
    // If no specific issues found, don't add to system_failure
    if (found.length > 0) {
      // Debug: Log all found columns for units with drain pan issues
      if (row.issues && row.issues.includes && row.issues.includes('Drain Pan')) {
        console.log(`[RiserHealthChart] Unit ${row.unit}: found array contains:`, found);
      }
      
      // actualIssueCounts => integer increments
      found.forEach((col) => {
        if (col === 'drip_pan__sensor' || col === 'drip_pan_sensor') {
          console.log(`[RiserHealthChart] Incrementing ${col} for riser ${riser}, unit ${row.unit}`);
        }
        actualIssueCounts[riser][col] += 1;
        if (col === 'drip_pan__sensor' || col === 'drip_pan_sensor') {
          console.log(`[RiserHealthChart] After increment, count for ${col} in riser ${riser}:`, actualIssueCounts[riser][col]);
        }
      });
    }
  });

  // 2) Now fill fractionalIssueCounts (each row => 1 / totalRows)
  Object.keys(riserAllRows).forEach((riser) => {
    const rows = riserAllRows[riser];
    const totalRows = rows.length;
    if (totalRows === 0) return;

    // Calculate the total percentage of units with issues in this riser
    const unitsWithIssues = rows.filter(r => r.inspection !== 'OK').length;
    const totalIssuePercentage = unitsWithIssues / totalRows;

    // Count total issues by type for this riser
    const issueTypeCounts = {};
    issueCols.forEach(col => {
      issueTypeCounts[col] = 0;
    });

    rows.forEach((row) => {
      if (row.inspection === 'OK') return;
      let found = [];
      if (dataFormat === "new" && row.rawData) {
        // New format: check if issue fields contain "HAS ISSUES" or 🔴
        // Map old column names to possible new field name keywords
        const fieldKeywordMap = {
          'thermostat': ['thermostat'],
          'blower_fan': ['blower_fan'],
          'actuator': ['actuator'],
          'n3way_valves': ['3_way_valve', '3_way_valves', 'field_3_way_valves', 'n3way_valves'],
          'drain_pan': ['drain_pan'], // Will be handled separately for drain pan
          'valves': ['valves'],
          'hoses': ['hoses'],
          'mould': ['mould'],
          'coils': ['coils'],
          'compressor': ['compressor'],
          'glycol_leakage': ['glycol_leakage'],
          'leak_sensor': ['leak_sensor'],
          'insulation': ['insulation']
        };
        
        // Direct field name mapping for exact matches (for fields like "drain_pan" that are arrays)
        const directFieldMap = {
          'drip_pan__sensor': ['drain_pan'] // Column is 'drip_pan__sensor' but field in rawData is 'drain_pan'
        };
        
        // Description field mapping for drain pan
        const descriptionFieldMap = {
          'drip_pan__sensor': 'description_of_drain_pan_issue_be_specific'
        };
        
        found = issueCols.filter((col) => {
          const keywords = fieldKeywordMap[col] || [col];
          const directFields = directFieldMap[col] || [];
          const descriptionField = descriptionFieldMap[col];
          let hasIssue = false;
          let hasExplicitOK = false;
          
          // Special handling for drain pan (col is 'drip_pan__sensor' but we check 'drain_pan' field in rawData)
          if (col === 'drip_pan__sensor') {
            // First, check the drain_pan array field directly
            if (row.rawData.hasOwnProperty('drain_pan')) {
              const val = row.rawData['drain_pan'];
              if (val !== null && val !== undefined && val !== '') {
                let valStr = Array.isArray(val) ? val.join(" ") : String(val);
                valStr = valStr.toUpperCase().trim();
                
                // Check for explicit issue indicators
                if (valStr.includes("HAS ISSUES") || valStr.includes("🔴")) {
                  return true; // Found explicit issue
                }
                
                // If explicitly "OK", this is NOT an issue
                if (valStr.includes("OK") && valStr.includes("🟢")) {
                  return false; // Explicitly OK
                }
              }
            }
            
            // Second, check the description field
            if (descriptionField && row.rawData.hasOwnProperty(descriptionField)) {
              const val = row.rawData[descriptionField];
              if (val !== null && val !== undefined && val !== '') {
                let valStr = Array.isArray(val) ? val.join(" ") : String(val);
                valStr = valStr.trim();
                
                // If there's a non-empty description, it indicates an issue
                if (valStr.length > 0 && !valStr.toUpperCase().includes("NO ISSUES")) {
                  return true;
                }
              }
            }
            
            // If neither field indicates an issue, return false
            return false;
          }
          
          // For other issue types, check all keys in rawData for fields that contain any of the keywords
          for (const key in row.rawData) {
            const keyLower = key.toLowerCase();
            // Skip photo fields - they don't indicate issues by themselves
            if (keyLower.includes('photo') || keyLower.includes('image')) {
              continue;
            }
            
            // Check if this key contains any of the keywords for this issue type
            const matchesKeyword = keywords.some(keyword => {
              const keywordLower = keyword.toLowerCase();
              // Check if key contains the keyword (e.g., "drip_pan" in "drip_pan_sensor_condition")
              return keyLower.includes(keywordLower);
            });
            
            if (matchesKeyword) {
              const val = row.rawData[key];
              if (val !== null && val !== undefined && val !== '') {
                let valStr = Array.isArray(val) ? val.join(" ") : String(val);
                valStr = valStr.toUpperCase().trim();
                
                // Check for explicit issue indicators
                if (valStr.includes("HAS ISSUES") || valStr.includes("🔴")) {
                  hasIssue = true;
                  break; // Found explicit issue, no need to check further
                }
                
                // If explicitly "OK", mark that we found an OK status
                if (valStr === "OK" || (valStr.includes("OK") && valStr.includes("🟢"))) {
                  hasExplicitOK = true;
                  // Don't break - continue checking other fields for explicit issues
                }
              }
            }
          }
          
          // If we found an explicit issue, return true
          if (hasIssue) {
            return true;
          }
          
          // If we found explicit OK, this is NOT an issue
          if (hasExplicitOK) {
            return false;
          }
          
          return false;
        });
      } else {
        // Old format: check if boolean/string flag indicates an issue
        found = issueCols.filter((col) => rowHasIssue(row, col));
      }
      found.forEach((col) => {
        issueTypeCounts[col]++;
      });
    });

    // Calculate the proportion of each issue type within the total issues
    const totalIssues = Object.values(issueTypeCounts).reduce((sum, count) => sum + count, 0);
    
    if (totalIssues > 0) {
      issueCols.forEach((col) => {
        const issueProportion = issueTypeCounts[col] / totalIssues;
        fractionalIssueCounts[riser][col] = totalIssuePercentage * issueProportion;
      });
    }
  });

  // 3) Sort risers numerically
  const risersSorted = Object.keys(riserAllRows).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  );

  // Filter out risers with 2 or fewer units to avoid false alarms
  // But for new format or when we have limited data, show all risers
  const filteredRisersSorted = risersSorted.filter((riser) => {
    const rows = riserAllRows[riser] || [];
    // For new format, show risers with at least 1 unit (less strict filtering)
    if (dataFormat === "new") {
      return rows.length > 0;
    }
    // For old format, keep the original filter
    return rows.length > 2;
  });
  
  console.log('[RiserHealthChart] Debug:', {
    dataFormat,
    totalUnits: unitsData.length,
    risersFound: risersSorted.length,
    filteredRisers: filteredRisersSorted.length,
    riserData: Object.keys(riserAllRows).map(r => ({
      riser: r,
      unitCount: riserAllRows[r].length,
      units: riserAllRows[r].map(u => u.unit)
    }))
  });

  // 4) Build stacked bar data
  // Filter out columns that have 0 issues across all risers
  const usedIssueCols = issueCols.filter((col) => {
    let sum = 0;
    filteredRisersSorted.forEach((r) => {
      sum += actualIssueCounts[r][col] || 0;
    });
    if (col === 'drip_pan__sensor' || col === 'drip_pan_sensor') {
      console.log(`[RiserHealthChart] Checking ${col} - sum: ${sum}`);
      filteredRisersSorted.forEach((r) => {
        if (actualIssueCounts[r][col] > 0) {
          console.log(`[RiserHealthChart] Riser ${r} has ${actualIssueCounts[r][col]} ${col} issues`);
        }
      });
    }
    return sum > 0;
  });

  console.log('[RiserHealthChart] Issue detection:', {
    usedIssueCols,
    actualIssueCounts: Object.keys(actualIssueCounts).reduce((acc, riser) => {
      acc[riser] = actualIssueCounts[riser];
      return acc;
    }, {})
  });

  // Each dataset => fraction for that col in each riser
  const datasets = usedIssueCols.map((col) => {
    const idx = issueCols.indexOf(col);
    return {
      label: friendlyLabels[col],
      data: filteredRisersSorted.map((r) => fractionalIssueCounts[r][col]),
      backgroundColor: COLOR_PALETTE[idx],
    };
  });

  const barData = {
    labels: filteredRisersSorted,
    datasets,
  };
  
  console.log('[RiserHealthChart] Chart data:', {
    labels: barData.labels,
    datasetsCount: barData.datasets.length,
    hasData: barData.labels.length > 0 && barData.datasets.length > 0
  });

  // 5) Identify risers with > 40% issues overall (or 100% if only 1-2 units)
  const problemRiserLines = [];
  let hasProblem = false;
  filteredRisersSorted.forEach((riser) => {
    const rows = riserAllRows[riser] || [];
    const totalRows = rows.length;
    if (totalRows === 0) return;

    // how many rows are not 'OK'
    const issueCount = rows.filter(r => r.inspection !== 'OK').length;
    if (issueCount === 0) return;

    const fraction = issueCount / totalRows;
    const pct = Math.round(fraction * 100);
    
    // For risers with 1-2 units: flag if 100% have issues
    // For risers with 3+ units: flag if > 40% have issues
    const shouldFlag = totalRows <= 2 
      ? pct === 100  // 100% of units have issues
      : pct > 40;    // More than 40% have issues
    
    if (shouldFlag) {
      hasProblem = true;
      problemRiserLines.push(
        <div key={riser}>
          <strong>{pct}%</strong> of {hvacType === 'heatpump' ? 'Heatpumps' : 'Fancoils'} in Riser {riser} (
          <strong>{issueCount}</strong> of <strong>{totalRows}</strong>) had identified issues
        </div>
      );
    }
  });

  // 6) We want the Y-axis max to be "round up to next 10%" above the tallest bar.
  //    We need to use the actual percentage of units with issues, not the stacked bar fractions.
  let maxPercentage = 0;
  filteredRisersSorted.forEach((riser) => {
    const rows = riserAllRows[riser] || [];
    const totalRows = rows.length;
    if (totalRows === 0) return;

    // how many rows are not 'OK'
    const issueCount = rows.filter(r => r.inspection !== 'OK').length;
    if (issueCount === 0) return;

    const fraction = issueCount / totalRows;
    const pct = Math.round(fraction * 100);
    if (pct > maxPercentage) {
      maxPercentage = pct;
    }
  });
  // round up to next 10
  let maxPct = Math.ceil(maxPercentage / 10) * 10; // e.g. 57 => 60
  if (maxPct > 100) maxPct = 100; // never exceed 100
  const maxYValue = maxPct / 100; // convert back to 0..1

  // 7) In the tooltip, we want the fraction of the *riser's total issues* (not total units).
  //    totalIssueCount = sum of actualIssueCounts[riser][col] for usedIssueCols.
  //    col portion = actualIssueCounts[riser][col].
  //    fractionOfIssues = (col portion / totalIssueCount) * 100 => sums to 100% across columns.
  const deviceLabel = hvacType === 'heatpump' ? 'Heatpumps' : 'Fancoils';

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const riserKey = filteredRisersSorted[ctx.dataIndex];
            const colName = usedIssueCols[ctx.datasetIndex];
            const colLabel = friendlyLabels[colName];
            
            const colCount = actualIssueCounts[riserKey][colName];
            // total issues in that riser
            const totalIssueCount = usedIssueCols.reduce(
              (acc, c) => acc + actualIssueCounts[riserKey][c],
              0
            );
            if (totalIssueCount === 0) {
              return `${colLabel}: 0 issues (0%)`;
            }
            const frac = (colCount / totalIssueCount) * 100;
            const pctStr = Math.round(frac);
            return `${colLabel}: ${colCount} issues, ${pctStr}% of this riser's issues`;
          }
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        title: {
          display: true,
          text: 'Riser Number',
          color: '#999',
          font: { size: 14, weight: 'bold' },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: maxYValue,    // <-- the custom top we computed
        ticks: {
          callback: (val) => {
            return Math.round(val * 100) + '%';
          },
          stepSize: 0.1,
        },
        grid: { display: true, color: '#f0f0f0' },
        title: {
          display: true,
          text: `% of ${deviceLabel} with Issues`,
          color: '#999',
          font: { size: 14, weight: 'bold' },
        },
      },
    },
  };

  // For the "Inspection Recommended" hover
  const inspectionTooltip = (
    <Tooltip id="tooltip-inspection">
      A high percentage of {deviceLabel.toLowerCase()} in these risers have identified issues.
      We recommend scheduling an inspection to check for potential riser-wide problems or leaks.
    </Tooltip>
  );

  // 8) Styles for badges
  const warningBadgeStyle = {
    backgroundColor: '#fef3f2',
    color: '#d92d20',
    border: 'none',
    fontWeight: '600',
    padding: '0.35rem 0.75rem',
    borderRadius: '9999px',
    marginLeft: '0.5rem',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
  };
  const healthyBadgeStyle = {
    backgroundColor: '#ecfdf5',
    color: '#00704d',
    border: 'none',
    fontWeight: '600',
    padding: '0.35rem 0.75rem',
    borderRadius: '9999px',
    marginLeft: '0.5rem',
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
        padding: '1rem',
        width: '100%',
      }}
    >
      {/* Title + badge */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h5 style={{ margin: 0, fontWeight: '600' }}>Riser Health</h5>
        {hasProblem ? (
          <OverlayTrigger placement="top" overlay={inspectionTooltip}>
            <span style={warningBadgeStyle}>
              Inspection Recommended
              <span style={{ marginLeft: '6px' }}>
                <i className="fa fa-info-circle" aria-hidden="true"></i>
              </span>
            </span>
          </OverlayTrigger>
        ) : (
          <span style={healthyBadgeStyle}>Healthy</span>
        )}
      </div>

      {/* Problem lines */}
      {problemRiserLines.length > 0 && (
        <div style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
          {problemRiserLines}
        </div>
      )}

      {/* Chart */}
      <div style={{ width: '100%', height: '400px' }}>
        {filteredRisersSorted.length > 0 && usedIssueCols.length > 0 ? (
          <Bar data={barData} options={options} />
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: '#999',
            fontSize: '0.9rem'
          }}>
            {filteredRisersSorted.length === 0 
              ? 'Not enough units to display riser data (need units from multiple risers)' 
              : 'No issues detected in riser data'}
          </div>
        )}
      </div>
    </div>
  );
}

export default RiserHealthChart;
